(() => {
  if (typeof window.FaraRadio?.wasStarted === 'function') return;
  const root = document.getElementById('FaraRadio');
  if (!root) return;
  const labels = JSON.parse(document.getElementById('FaraRadioLabels').textContent);
  const toggle = root.querySelector('[data-radio-play]');
  const nextButton = root.querySelector('[data-radio-next]');
  const stateNode = root.querySelector('[data-radio-state]');
  const nowNode = root.querySelector('[data-radio-now]');
  const statusNode = root.querySelector('[data-radio-status]');
  const shopNode = root.querySelector('[data-radio-shop]');
  const tickerNode = root.querySelector('[data-radio-ticker]');
  const tickerTrack = root.querySelector('[data-radio-ticker-track]');
  const tickerRuns = root.querySelectorAll('[data-radio-run]');
  const motionButton = root.querySelector('[data-radio-motion]');
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const shopFallback = shopNode.href;
  const publishedProducts = new Map();
  const storageKey = 'fara-radio-session-v1';
  let audio, tracks, queue = [], current = null, failures = 0, started = false, navigationLoading;
  let pendingSeek = 0, lastSave = 0, gatedNavigation = 0;
  let tickerPaused = false, tickerText = '';
  let playbackIntended = false, playAttempt = 0;
  let selectionHeard = false;
  let issue = null, completedIssueId = '';
  try { tickerPaused = sessionStorage.getItem('fara-radio-ticker-paused') === 'true'; } catch {}
  const announce = message => { statusNode.textContent = message; };
  async function updateShop(track) {
    shopNode.href = shopFallback;
    tickerNode.href = shopFallback;
    shopNode.textContent = labels.shop + ' ↗';
    const productUrl = (root.dataset.rootUrl || '/') + 'products/' + encodeURIComponent(track.handle);
    if (!publishedProducts.has(track.handle)) {
      publishedProducts.set(track.handle, fetch(productUrl + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(response => response.ok ? response.json() : null)
        .then(product => product?.handle === track.handle)
        .catch(() => false));
    }
    if (await publishedProducts.get(track.handle) && current === track) {
      shopNode.href = productUrl;
      tickerNode.href = productUrl;
      shopNode.textContent = labels.shop_track + ' ↗';
    }
  }
  function loadTracks() {
    if (tracks) return tracks;
    const data = JSON.parse(document.getElementById('FaraRadioPlaylist').textContent);
    tracks = data.tracks.map(row => ({ title: row[0], artist: row[1], cat: row[2], handle: row[3], src: data.base + row[4] }));
    return tracks;
  }
  function paint(message) {
    const playing = !!audio && !audio.paused && !audio.error;
    root.classList.toggle('is-playing', playing);
    toggle.setAttribute('aria-pressed', String(playing));
    toggle.setAttribute('aria-label', playing ? labels.pause : labels.play);
    stateNode.textContent = playing ? labels.playing : labels.paused;
    const nowText = message || (current ? current.title + ' — ' + current.artist : labels.idle);
    if (nowNode.textContent !== nowText) nowNode.textContent = nowText;
    paintTicker(playing, message);
    if (navigator.mediaSession) navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
    window.dispatchEvent(new CustomEvent('fara:radio-state', { detail: getState() }));
  }
  function paintTicker(playing, message) {
    const nextTrack = issue ? tracks[issue.indices[issue.index + 1]] : current && tracks && queue.length ? tracks[queue[0]] : null;
    const tag = message === labels.error ? labels.signal_lost : playing ? labels.on_air : labels.paused;
    const now = message || (current ? current.title + ' — ' + current.artist : labels.ticker_idle);
    const parts = [];
    if (current || message) parts.push(['fara-radio__tag ' + (playing || message === labels.error ? 'fara-radio__tag--live' : 'fara-radio__tag--paused'), tag]);
    parts.push(['fara-radio__track-name', now]);
    if (current && !message) parts.push(['fara-radio__cat', current.cat]);
    if (nextTrack && !message) {
      parts.push(['fara-radio__tag fara-radio__tag--next', labels.up_next]);
      parts.push(['fara-radio__soon', nextTrack.title + ' — ' + nextTrack.artist]);
    }
    const plain = parts.map(part => part[1]).join(' ');
    if (plain === tickerText) return;
    tickerText = plain;
    tickerNode.setAttribute('aria-label', plain);
    tickerRuns.forEach(run => {
      run.replaceChildren(...parts.map(([className, value]) => {
        const span = document.createElement('span');
        span.className = className;
        span.textContent = value;
        return span;
      }));
    });
    tickerTrack.style.setProperty('--fara-radio-speed', Math.max(24, Math.min(80, Math.round(plain.length * 0.42))) + 's');
  }
  function syncTickerMotion() {
    const reduced = motionPreference.matches;
    const paused = tickerPaused || reduced;
    root.classList.toggle('is-ticker-paused', paused);
    motionButton.disabled = reduced;
    motionButton.setAttribute('aria-pressed', String(paused));
    const label = reduced ? labels.ticker_reduced : tickerPaused ? labels.resume_ticker : labels.pause_ticker;
    motionButton.setAttribute('aria-label', label);
    motionButton.title = label;
  }
  function save() {
    if (!current || !audio) return;
    try { sessionStorage.setItem(storageKey, JSON.stringify({ handle: current.handle, time: pendingSeek || audio.currentTime || 0, playing: !audio.paused, started, issue: issue && {id: issue.id, title: issue.title, handles: issue.handles, index: issue.index, completeMessage: issue.completeMessage} })); } catch {}
  }
  function enableNavigation() {
    if (window.Shopify?.designMode) return Promise.resolve();
    if (navigationLoading) return navigationLoading;
    navigationLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = root.dataset.navigationUrl;
      const timeout = setTimeout(() => { script.remove(); reject(new Error('Navigation unavailable')); }, 10000);
      script.onload = () => { clearTimeout(timeout); resolve(); };
      script.onerror = () => { clearTimeout(timeout); reject(new Error('Navigation unavailable')); };
      document.head.append(script);
    }).catch(() => { navigationLoading = null; });
    return navigationLoading;
  }
  function getAudio() {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'none';
    audio.addEventListener('play', () => {
      if (!playbackIntended) { audio.pause(); paint(); return; }
      started = true;
      window.dispatchEvent(new CustomEvent('sx:audio-play', { detail: { source: 'radio' } }));
      if (!audio.paused) document.querySelectorAll('audio').forEach(element => element.pause());
      enableNavigation(); paint(); save();
    });
    audio.addEventListener('playing', () => {
      failures = 0;
      paint();
      // A queued event from the previous source must not count the new selection.
      if (selectionHeard || !playbackIntended || audio.paused || audio.error || audio.readyState < 3 || !current || audio.currentSrc !== new URL(current.src, location.href).href) return;
      selectionHeard = true;
      window.dispatchEvent(new CustomEvent('fara:record-heard', { detail: { handle: current.handle, title: current.title, artist: current.artist, source: 'radio' } }));
    });
    audio.addEventListener('pause', () => { paint(); save(); });
    audio.addEventListener('ended', () => { if (playbackIntended) next(true); else paint(); });
    audio.addEventListener('error', () => {
      if (!playbackIntended) { paint(); return; }
      if (++failures < 5) next(true);
      else { pause(); paint(labels.error); announce(labels.error); }
    });
    audio.addEventListener('loadedmetadata', () => {
      if (pendingSeek) {
        try { audio.currentTime = Math.min(pendingSeek, Number.isFinite(audio.duration) ? audio.duration : pendingSeek); } catch {}
        pendingSeek = 0;
      }
    });
    audio.addEventListener('timeupdate', () => { if (Date.now() - lastSave > 4000) { lastSave = Date.now(); save(); } });
    if (navigator.mediaSession) {
      for (const [name, handler] of Object.entries({ play: () => play(), pause, nexttrack: () => { failures = 0; next(true); } })) {
        try { navigator.mediaSession.setActionHandler(name, handler); } catch {}
      }
    }
    return audio;
  }
  function refill() {
    queue = loadTracks().map((_, index) => index);
    for (let index = queue.length - 1; index > 0; index--) {
      const swap = Math.floor(Math.random() * (index + 1));
      [queue[index], queue[swap]] = [queue[swap], queue[index]];
    }
    if (queue.length > 1 && tracks[queue[0]]?.handle === current?.handle) [queue[0], queue[1]] = [queue[1], queue[0]];
  }
  async function play() {
    started = true;
    playbackIntended = true;
    enableNavigation();
    const player = getAudio();
    if (!current) { next(true); return; }
    if (player.error) player.src = current.src;
    const attempt = ++playAttempt;
    try { await player.play(); }
    catch { if (attempt === playAttempt && playbackIntended) { paint(labels.retry); announce(labels.retry); } }
  }
  function pause() { playbackIntended = false; playAttempt++; if (audio) audio.pause(); else paint(); }
  function getState() {
    return {
      playing: !!audio && !audio.paused && !audio.error,
      intended: playbackIntended,
      track: current && {title: current.title, artist: current.artist, handle: current.handle},
      issue: issue && {id: issue.id, title: issue.title, index: issue.index, handles: [...issue.handles]},
      completedIssueId
    };
  }
  function readIssue(value) {
    if (!value || !/^[a-z0-9][a-z0-9-]{0,63}$/.test(value.id || '') || typeof value.title !== 'string' || !Array.isArray(value.handles) || !value.handles.length || value.handles.length > 24) return null;
    if (value.handles.some(handle => typeof handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(handle)) || new Set(value.handles).size !== value.handles.length) return null;
    const available = loadTracks();
    const indices = value.handles.map(handle => available.findIndex(track => track.handle === handle));
    if (indices.some(index => index < 0)) return null;
    return {id: value.id, title: value.title.slice(0, 120), handles: [...value.handles], indices, index: 0, completeMessage: typeof value.completeMessage === 'string' ? value.completeMessage.slice(0, 240) : ''};
  }
  function startIssue(definition, index = 0) {
    const selection = readIssue(definition);
    if (!selection || !Number.isInteger(index) || index < 0 || index >= selection.indices.length) return Promise.resolve(false);
    issue = selection;
    issue.index = index;
    completedIssueId = '';
    failures = 0;
    select(tracks[issue.indices[index]]);
    return play();
  }
  function select(track) {
    playAttempt++;
    current = track;
    selectionHeard = false;
    if (!queue.length) refill();
    pendingSeek = 0;
    getAudio().src = track.src;
    updateShop(track);
    if (navigator.mediaSession && window.MediaMetadata) {
      try { navigator.mediaSession.metadata = new MediaMetadata({ title: track.title, artist: track.artist, album: labels.name + ' · ' + track.cat }); } catch {}
    }
    paint();
  }
  function next(shouldPlay) {
    if (issue) {
      if (issue.index + 1 < issue.indices.length) {
        issue.index++;
        select(tracks[issue.indices[issue.index]]);
        if (shouldPlay) return play();
        return;
      }
      const completion = issue.completeMessage;
      completedIssueId = issue.id;
      issue = null;
      next(shouldPlay);
      if (completion) announce(completion);
      return;
    }
    if (!queue.length) refill();
    if (!queue.length) { paint(labels.error); return; }
    select(tracks[queue.shift()]);
    if (shouldPlay) return play();
  }
  toggle.addEventListener('click', () => {
    failures = 0;
    if (audio && (!audio.paused || playbackIntended)) pause();
    else play();
  });
  nextButton.addEventListener('click', () => { failures = 0; next(true); });
  motionButton.addEventListener('click', () => {
    tickerPaused = !tickerPaused;
    try { sessionStorage.setItem('fara-radio-ticker-paused', String(tickerPaused)); } catch {}
    syncTickerMotion();
  });
  motionPreference.addEventListener('change', syncTickerMotion);
  syncTickerMotion();
  paint();
  window.addEventListener('sx:audio-play', event => {
    if (event.detail?.source !== 'radio') pause();
  });
  document.addEventListener('click', event => {
    if (!started || window.FaraRadioNavigation || window.Shopify?.designMode || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') || link.matches('[data-no-router],[data-fara-native-link]') || link.target && link.target !== '_self') return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || !/^https?:$/.test(url.protocol) || url.pathname === location.pathname && url.search === location.search && url.hash) return;
    const prefix = window.Shopify?.routes?.root || '/';
    const path = prefix !== '/' && url.pathname.startsWith(prefix) ? '/' + url.pathname.slice(prefix.length) : url.pathname;
    if (!/^(?:\/$|\/cart$|\/search$|\/(?:products|collections|pages|blogs|policies)(?:\/|$))/.test(path)) return;
    event.preventDefault();
    const token = ++gatedNavigation;
    enableNavigation().then(() => {
      if (token !== gatedNavigation) return;
      if (typeof window.FaraRadioNavigation?.navigate === 'function') window.FaraRadioNavigation.navigate(url.href);
      else location.assign(url.href);
    });
  });
  window.addEventListener('pagehide', save);
  window.FaraRadio = { pause, resume: play, next: () => { failures = 0; return next(true); }, startIssue, getState, isPlaying: () => !!audio && !audio.paused, wasStarted: () => started, announce, labels, save };
  window.dispatchEvent(new CustomEvent('fara:radio-state', {detail: getState()}));
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if (saved?.started && saved.handle) {
      const track = loadTracks().find(entry => entry.handle === saved.handle);
      if (track) {
        const restored = readIssue(saved.issue);
        if (restored && Number.isInteger(saved.issue.index) && restored.handles[saved.issue.index] === track.handle) {
          issue = restored;
          issue.index = saved.issue.index;
        }
        select(track); pendingSeek = Math.max(0, Number(saved.time) || 0); started = true; if (saved.playing && !window.Shopify?.designMode) play();
      }
    }
  } catch {}
})();
